import { Icon } from "@captions-web/icons";
import { createFileRoute } from "@tanstack/react-router";
import { Fragment, useRef, useState } from "react";

import { DropZone } from "~/components/drop-zone";
import { ImageCropper, ImageCropperRefHandle } from "~/components/image-cropper";
import { Button } from "~/components/primitives/button";
import { css } from "~/styled-system/css";

export const Route = createFileRoute("/storybook/cropper")({
  component: RouteComponent,
});

function RouteComponent() {
  const [imageFile, setImageFile] = useState<File | null>(null);
  const cropperRef = useRef<ImageCropperRefHandle>(null);

  const handleImageDrop = (files: File[]) => {
    if (files.length > 0) {
      setImageFile(files[0]);
    }
  };

  const handleDownload = async () => {
    if (!cropperRef.current) {
      return;
    }

    try {
      const croppedFile = await cropperRef.current.cropImageToFile(720);
      const url = URL.createObjectURL(croppedFile);
      const a = document.createElement("a");
      a.href = url;
      a.download = "cropped-image.jpg";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download cropped image:", error);
    }
  };

  return (
    <div className={sectionStyles}>
      <h2 className={titleStyles}>Cropper</h2>
      <div className={wrapperStyles}>
        {imageFile ? (
          <Fragment>
            <ImageCropper
              ref={cropperRef}
              imageUrl={URL.createObjectURL(imageFile)}
              aspectRatio="9/16"
            />
            <Button variant="primary" onClick={handleDownload} className={downloadButtonStyles}>
              Download Cropped Image
            </Button>
          </Fragment>
        ) : (
          <DropZone acceptMultiple={false} onDrop={handleImageDrop} className={dropZoneStyles}>
            {() => (
              <div className={dropZoneContentStyles}>
                <Icon name="upload" size={48} />
                <p>Drag and drop an image or click to browse</p>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = "image/*";
                    input.onchange = (e) => {
                      const files = (e.target as HTMLInputElement).files;
                      if (files) {
                        handleImageDrop(Array.from(files));
                      }
                    };
                    input.click();
                  }}
                >
                  Browse Files
                </Button>
              </div>
            )}
          </DropZone>
        )}
      </div>
    </div>
  );
}

const sectionStyles = css({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  padding: "12px 16px 20px",

  // slightly different background to match that of the design system figma for better contrast
  background: "#181818",
  borderRadius: "8px",
  border: "1px solid token(colors.$gray.800)",
});

const titleStyles = css({
  fontSize: "30px",
  textShadow: "2px 2px 0 token(colors.$purple)",
});

const dropZoneStyles = css({
  width: "100%",
  height: "200px",
  border: "2px dashed $gray.600",
  borderRadius: "8px",
  cursor: "pointer",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    borderColor: "$gray.500",
  },
});

const wrapperStyles = css({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  maxWidth: "600px",
  height: "600px",
  margin: "40px auto",
});

const dropZoneContentStyles = css({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "12px",
  color: "$gray.400",
});

const downloadButtonStyles = css({
  marginTop: "16px",
});
